import type React from "react";
import { memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import Checkbox from "components/v2/Checkbox/Checkbox";
import Input from "components/v2/Input/Input";
import CbmInputFields from "@RequestFormV2/sections/PackingList/components/shared/CbmInputFields";
import DimensionFieldInput from "@RequestFormV2/sections/PackingList/components/shared/DimensionFieldInput";
import PackingListCell from "@RequestFormV2/sections/PackingList/components/shared/PackingListCell";
import PackingListCratingDatePicker from "@RequestFormV2/sections/PackingList/components/shared/PackingListCratingDatePicker";
import PackingListSelect from "@RequestFormV2/sections/PackingList/components/shared/PackingListSelect";
import PackingListStopSelect from "@RequestFormV2/sections/PackingList/components/shared/PackingListStopSelect";
import PackingListTableRow from "@RequestFormV2/sections/PackingList/components/shared/PackingListTableRow";
import DangerousGoodsSelect from "@RequestFormV2/sections/PackingList/components/shared/DangerousGoodsSelect";
import TemperatureSelect from "@RequestFormV2/sections/PackingList/components/shared/TemperatureSelect";
import DimensionUnitSelect from "@RequestFormV2/sections/PackingList/components/shared/DimensionUnitSelect";
import WeightUnitSelect from "@RequestFormV2/sections/PackingList/components/shared/WeightUnitSelect";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { HANDLING_UNIT_COLUMN_WIDTHS } from "@RequestFormV2/sections/PackingList/components/shared/packingListColumnWidths";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import { usePackingListRowReactiveClear } from "@RequestFormV2/sections/PackingList/hooks/usePackingListRowReactiveClear";
import PackingListRequiredHint from "@RequestFormV2/sections/PackingList/components/shared/PackingListRequiredHint";
import { applyHandlingUnitDefaults } from "@RequestFormV2/sections/PackingList/utils/applyHandlingUnitDefaults";
import { getPackingListFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";
import { getPackingListRequiredHintPaths } from "@RequestFormV2/sections/PackingList/utils/getPackingListRequiredHintPaths";
import { parseClipboardGrid, type PackingListPasteColumn } from "@RequestFormV2/sections/PackingList/utils/pastePackingList";
import {
  getPackingListWorkflowColumnFlags,
  isNmfcInputVisibleForRow,
} from "@RequestFormV2/sections/PackingList/utils/packingListWorkflowFlags";

type HandlingUnitTableRowProps = {
  index: number;
  fieldId: string;
  rowNumber: number;
  fallbackRow: PackingListRow;
  deleteDisabled: boolean;
  isMultiStop: boolean;
};

function HandlingUnitTableRow({
  index,
  fieldId,
  rowNumber,
  fallbackRow,
  deleteDisabled,
  isMultiStop,
}: HandlingUnitTableRowProps) {
  const intl = useIntl();
  const { control, setValue } = useFormContext();
  const {
    handlingUnitOptions,
    nmfcOptions,
    dimensionUnitOptions,
    weightUnitOptions,
    temperatureOptions,
    dangerousGoodsSelectOptions,
    packingListConfig,
    duplicateRow,
    deleteRow,
    handlePackingListFieldBlur,
    handlePackingListPaste,
    isFieldRequired,
    handlingUnitHashMap,
  } = usePackingList();
  const { hasAttemptedReview } = useRequestFormReviewValidation();
  const { cratingEnabled, hazardousEnabled, temperatureEnabled, dangerousEnabled, nmfcEnabled } =
    getPackingListWorkflowColumnFlags(packingListConfig);
  const nmfcNumberEnabled = packingListConfig?.nmfc_number_enabled;
  const currentRow = (useWatch({ name: `packing_list.${index}`, control }) as PackingListRow | undefined) ?? fallbackRow;
  const showNmfcInput = isNmfcInputVisibleForRow(currentRow, handlingUnitHashMap, nmfcNumberEnabled);
  const handlingUnitSettings = currentRow.handling_unit
    ? handlingUnitHashMap[currentRow.handling_unit.toLowerCase()]
    : undefined;
  const usesCbmInput = Boolean(handlingUnitSettings?.l_w_h_cbm);
  const dimensionsOptional = Boolean(
    currentRow.handling_unit &&
      handlingUnitSettings &&
      !usesCbmInput &&
      handlingUnitSettings.l_w_h !== "off" &&
      !isFieldRequired(currentRow, "dimensions"),
  );
  const cbmValueField = currentRow.cbm_scope === "total" ? "l_w_h_cbm_total" : "l_w_h_cbm_piece";

  usePackingListRowReactiveClear(index, currentRow, handlingUnitHashMap, nmfcNumberEnabled);
  const requiredHintPaths = getPackingListRequiredHintPaths(
    currentRow,
    index,
    handlingUnitHashMap,
    hasAttemptedReview,
    nmfcNumberEnabled,
  );
  const hasRequiredHint = (field: string) => requiredHintPaths.includes(getPackingListFieldPath(index, field));

  const handleCellPaste = (column: PackingListPasteColumn) => (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    const grid = parseClipboardGrid(text);
    const isMultiCell = grid.length > 1 || (grid[0]?.length ?? 0) > 1;
    if (!isMultiCell) return;

    event.preventDefault();
    handlePackingListPaste(index, column, text);
  };

  return (
    <PackingListTableRow
      rowNumber={rowNumber}
      onDuplicate={() => duplicateRow(fieldId)}
      onDelete={() => deleteRow(fieldId)}
      deleteDisabled={deleteDisabled}
    >
      <PackingListCell style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.handlingUnit }}>
        <PackingListSelect
          name={getPackingListFieldPath(index, "handling_unit")}
          hideError={hasRequiredHint("handling_unit")}
          options={handlingUnitOptions}
          placeholder={intl.formatMessage({
            id: "request-form.packing-list.selectHandlingUnit",
            defaultMessage: "Select handling unit",
          })}
          onChange={(value) => {
            applyHandlingUnitDefaults({
              setValue,
              index,
              settings: value ? handlingUnitHashMap[value.toLowerCase()] : undefined,
            });
            handlePackingListFieldBlur(fieldId);
          }}
        />
        {hasRequiredHint("handling_unit") && <PackingListRequiredHint />}
      </PackingListCell>
      <PackingListCell className="rfv2-packing-list-table__cell--dimensions">
        {usesCbmInput ? (
          <CbmInputFields
            index={index}
            showScopeRequiredHint={hasRequiredHint("cbm_scope")}
            showValueRequiredHint={hasRequiredHint(cbmValueField)}
            onChange={() => handlePackingListFieldBlur(fieldId)}
          />
        ) : (
          <>
            {(["length", "width", "height"] as const).map((dim) => (
              <div key={dim} style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.dimension }}>
                <DimensionFieldInput
                  name={getPackingListFieldPath(index, dim)}
                  dimensionLabel={dim[0].toUpperCase()}
                  showOptionalHint={dimensionsOptional}
                  showRequiredHint={hasRequiredHint(dim)}
                  onChange={() => handlePackingListFieldBlur(fieldId)}
                  onPaste={handleCellPaste(dim)}
                />
              </div>
            ))}
            <div style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.dimensionUnit }}>
              <DimensionUnitSelect
                index={index}
                options={dimensionUnitOptions}
                onChange={() => handlePackingListFieldBlur(fieldId)}
              />
            </div>
          </>
        )}
      </PackingListCell>
      <PackingListCell className="rfv2-packing-list-table__cell--weight">
        <div className="rfv2-packing-list-table__weight-input">
          <Input
            name={getPackingListFieldPath(index, "weight_piece")}
            type="number"
            placeholder="0.00"
            hideError={hasRequiredHint("weight_piece")}
            onChange={() => handlePackingListFieldBlur(fieldId)}
            onPaste={handleCellPaste("weight_piece")}
          />
          {hasRequiredHint("weight_piece") && <PackingListRequiredHint />}
        </div>
        <WeightUnitSelect index={index} options={weightUnitOptions} onChange={() => handlePackingListFieldBlur(fieldId)} />
      </PackingListCell>
      <PackingListCell className="rfv2-packing-list-table__cell--qty" style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.qty }}>
        <Input
          name={getPackingListFieldPath(index, "units")}
          type="number"
          hideError={hasRequiredHint("units")}
          onChange={() => handlePackingListFieldBlur(fieldId)}
          onPaste={handleCellPaste("units")}
        />
        {hasRequiredHint("units") && <PackingListRequiredHint />}
      </PackingListCell>
      {nmfcEnabled && (
        <PackingListCell style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.nmfc }}>
          {showNmfcInput && (
            <>
              <PackingListSelect
                size="sm"
                isClearable
                name={getPackingListFieldPath(index, "nmfc_class")}
                hideError={hasRequiredHint("nmfc_class")}
                options={nmfcOptions}
                placeholder={intl.formatMessage({
                  id: "request-form.packing-list.selectNmfc",
                  defaultMessage: "Select NMFC",
                })}
                onChange={() => handlePackingListFieldBlur(fieldId)}
              />
              {hasRequiredHint("nmfc_class") && <PackingListRequiredHint />}
            </>
          )}
        </PackingListCell>
      )}
      {isMultiStop && (
        <>
          <PackingListCell style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.stop }}>
            <PackingListStopSelect index={index} fieldId={fieldId} type="pickup" />
          </PackingListCell>
          <PackingListCell style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.stop }}>
            <PackingListStopSelect index={index} fieldId={fieldId} type="delivery" />
          </PackingListCell>
        </>
      )}
      <PackingListCell
        className="rfv2-packing-list-table__cell--center"
        style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.toggle }}
      >
        <Checkbox name={getPackingListFieldPath(index, "stackable")} />
      </PackingListCell>
      {hazardousEnabled && (
        <PackingListCell
          className="rfv2-packing-list-table__cell--center"
          style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.toggle }}
        >
          <Checkbox name={getPackingListFieldPath(index, "hazardous")} />
        </PackingListCell>
      )}
      {cratingEnabled && (
        <PackingListCell
          className="rfv2-packing-list-table__cell--crating"
          style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.temperature }}
        >
          <PackingListCratingDatePicker index={index} onChange={() => handlePackingListFieldBlur(fieldId)} />
        </PackingListCell>
      )}
      {temperatureEnabled && (
        <PackingListCell
          className="rfv2-packing-list-table__cell--temperature"
          style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.temperature }}
        >
          <TemperatureSelect index={index} options={temperatureOptions} onChange={() => handlePackingListFieldBlur(fieldId)} />
        </PackingListCell>
      )}
      {dangerousEnabled && (
        <>
          <PackingListCell
            className="rfv2-packing-list-table__cell--dangerous"
            style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.dangerous }}
          >
            <DangerousGoodsSelect
              index={index}
              options={dangerousGoodsSelectOptions}
              onChange={() => handlePackingListFieldBlur(fieldId)}
            />
          </PackingListCell>
          <PackingListCell style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.unNumber }}>
            <Input
              name={getPackingListFieldPath(index, "un_number")}
              disabled={!currentRow.dangerous_good_value}
              onChange={() => handlePackingListFieldBlur(fieldId)}
            />
          </PackingListCell>
        </>
      )}
      <PackingListCell style={{ width: HANDLING_UNIT_COLUMN_WIDTHS.additional }}>
        <Input
          name={getPackingListFieldPath(index, "additional_information")}
          hideError={hasRequiredHint("additional_information")}
          onChange={() => handlePackingListFieldBlur(fieldId)}
        />
        {hasRequiredHint("additional_information") && <PackingListRequiredHint />}
      </PackingListCell>
    </PackingListTableRow>
  );
}

export default memo(HandlingUnitTableRow);
